import Toggle from 'components/ReusableComponents/Toggle'
import { useInternalAdmin } from 'hooks'
import { useEffect, useReducer } from 'react'
import { useToasts } from 'react-toast-notifications'

import CreditCardInput from '../../CreditCardInput/index'
import CurrencyInput from '../../CurrencyInput/index'
import TextInput from '../../ManageHotel/TextInput/index'
import inputStyles from '../inputs.module.scss'
import styles from './CreditCardForm.module.scss'
import { ReactComponent as InfoIcon } from './info.svg'

const defaultInitialState = {
  pan: '',
  uiPan: '',
  expirationMonth: '',
  expirationYear: '',
  expDate: '',
  network: '',
  holder: '',
  uiAmount: 0,
  uiChargeAmount: 0,
  amount: 0,
  chargeAmount: 0,
  isOneTimeCard: false
}

function reducer(state, action) {
  switch (action.type) {
    case 'CC_NUMBER_CHANGE':
      return {
        ...state,
        pan: action.payload.replace(/\s/g, ''),
        uiPan: action.payload
      }
    case 'TYPE_CHANGE':
      return { ...state, network: action.payload }
    case 'CVV_CHANGE':
      return { ...state, cvv: action.payload }
    case 'EXP_DATE_CHANGE':
      return {
        ...state,
        expDate: action.payload,
        expirationMonth: action.payload.match(/^\d{2}/)
          ? action.payload.match(/^\d{2}/)[0]
          : '',
        expirationYear: action.payload.match(/\d{2}$/)
          ? `20${action.payload.match(/\d{2}$/)[0]}`
          : ''
      }
    case 'HOLDER_NAME_CHANGE':
      return { ...state, holder: action.payload }
    case 'AMOUNT_CHANGE':
      return {
        ...state,
        uiAmount: action.payload,
        amount: action.payload.replace(/[$,]/gi, '')
      }
    case 'CHARGE_AMOUNT_CHANGE':
      return {
        ...state,
        uiChargeAmount: action.payload,
        chargeAmount: action.payload.replace(/[$,]/gi, '')
      }
    case 'ONE_TIME_CARD_CHANGE':
      return {
        ...state,
        isOneTimeCard: action.payload
      }
    case 'ERROR':
      return { ...state, error: action.payload }
    default:
      throw new Error()
  }
}

const CreditCardForm = ({
  handleChange,
  defaultValues = defaultInitialState,
  isActivation,
  usedSavedCC,
  isAmountLocked,
  isAutoPay
}) => {
  const isInternalAdmin = useInternalAdmin()
  const [state, dispatch] = useReducer(reducer, defaultValues)
  const { addToast } = useToasts()

  useEffect(() => {
    if (handleChange) {
      handleChange(state)
    }
  }, [handleChange, state])

  useEffect(() => {
    if (isActivation) {
      dispatch({ type: 'AMOUNT_CHANGE', payload: `0.01` })
      dispatch({ type: 'CHARGE_AMOUNT_CHANGE', payload: `0.01` })
    }
  }, [isActivation])

  useEffect(() => {
    if (!isInternalAdmin) {
      return
    }

    if (state.holder.length > 50) {
      addToast('Cardholder name cannot be more than 50 characters', {
        appearance: 'error',
        autoDismiss: true
      })
    }
    if (parseFloat(state.chargeAmount) < parseFloat(state.amount)) {
      addToast('Charge amount cannot be less than amount to pay', {
        appearance: 'error',
        autoDismiss: true
      })
    }
  }, [addToast, isInternalAdmin, state])

  return (
    <div className={styles.container}>
      {isActivation ? (
        <div className={styles.activationMessage}>
          <InfoIcon />
          You may receive a 1.00 USD charge as a temporary authorization to validate your
          card
        </div>
      ) : (
        <div className={styles.currenyRow}>
          <label className={styles.currencyLabel}>
            <span className={styles.currencyLabelText}>Amount to pay</span>
            <CurrencyInput
              onChange={e => {
                dispatch({ type: 'AMOUNT_CHANGE', payload: e.target.value })
                if (!isInternalAdmin) {
                  dispatch({ type: 'CHARGE_AMOUNT_CHANGE', payload: e.target.value })
                }
              }}
              value={state.uiAmount}
              className={styles.currencyInput}
              disabled={isAmountLocked}
            />
          </label>
          {isInternalAdmin && (
            <label className={styles.currencyLabel}>
              <span className={styles.currencyLabelText}>Amount to Charge</span>
              <CurrencyInput
                onChange={e =>
                  dispatch({ type: 'CHARGE_AMOUNT_CHANGE', payload: e.target.value })
                }
                value={state.uiChargeAmount}
                className={styles.currencyInput}
              />
            </label>
          )}
        </div>
      )}
      <TextInput
        className={inputStyles.input}
        value={usedSavedCC ? usedSavedCC.accountHolderName : state.holder}
        disabled={!!usedSavedCC}
        label="Cardholder's name"
        maxLength={50}
        onChange={e => dispatch({ type: 'HOLDER_NAME_CHANGE', payload: e.target.value })}
      />
      {usedSavedCC ? (
        <TextInput
          className={inputStyles.input}
          value={usedSavedCC.friendlyDisplayName}
          disabled
        />
      ) : (
        <div className={styles.ccInputHolder}>
          <CreditCardInput
            handleCardNumberChange={e =>
              dispatch({ type: 'CC_NUMBER_CHANGE', payload: e.target.value })
            }
            handleCardTypeChange={e =>
              state.network !== e ? dispatch({ type: 'TYPE_CHANGE', payload: e }) : false
            }
            handleExpiryDateChange={e =>
              dispatch({ type: 'EXP_DATE_CHANGE', payload: e.target.value })
            }
            handleCVCchange={e =>
              dispatch({ type: 'CVV_CHANGE', payload: e.target.value })
            }
            handleErred={error =>
              state.error !== error ? dispatch({ type: 'ERROR', payload: error }) : false
            }
          />
          {isInternalAdmin && !isAutoPay && (
            <div className={styles.toggle}>
              <Toggle
                label="One time card"
                checked={state.isOneTimeCard}
                handleChange={e =>
                  dispatch({ type: 'ONE_TIME_CARD_CHANGE', payload: e.target.checked })
                }
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default CreditCardForm
